import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Wallet, TrendingUp, TrendingDown, DollarSign, Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default function Financials() {
  const { getAuthHeader, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [financials, setFinancials] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  // Ref for the entry form so the "Edit" button can scroll it into view.
  const entryFormRef = useRef(null);
  // Tracks the row currently being edited (for highlight/UX). null = "adding new".
  const [editingMonth, setEditingMonth] = useState(null);
  
  // Form state for new entry
  const [newEntry, setNewEntry] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    accounts_receivable: "",
    work_in_progress: "",
    cash_balance: ""
  });

  useEffect(() => {
    loadFinancials();
  }, [selectedYear]);

  const loadFinancials = async () => {
    setLoading(true);
    try {
      const [finRes, compRes] = await Promise.all([
        axios.get(`${API_URL}/api/financials?year=${selectedYear}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/financials/comparison?year=${selectedYear}`, { headers: getAuthHeader() })
      ]);
      setFinancials(finRes.data);
      setComparison(compRes.data);
    } catch (error) {
      toast.error("Failed to load financials");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newEntry.accounts_receivable && !newEntry.work_in_progress && !newEntry.cash_balance) {
      toast.error("Please enter at least one value");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/financials`, {
        year: parseInt(newEntry.year),
        month: parseInt(newEntry.month),
        accounts_receivable: parseFloat(newEntry.accounts_receivable) || 0,
        work_in_progress: parseFloat(newEntry.work_in_progress) || 0,
        cash_balance: parseFloat(newEntry.cash_balance) || 0
      }, { headers: getAuthHeader() });
      
      toast.success("Financials saved");
      loadFinancials();
      setNewEntry({
        ...newEntry,
        accounts_receivable: "",
        work_in_progress: "",
        cash_balance: ""
      });
      setEditingMonth(null);
    } catch (error) {
      toast.error("Failed to save financials");
    } finally {
      setSaving(false);
    }
  };

  // Prefill the entry form with an existing row's values so the admin can edit
  // it. The backend upserts on (year, month), so hitting Save overwrites cleanly.
  const startEditRow = (row) => {
    setNewEntry({
      year: row.year || selectedYear,
      month: row.month,
      accounts_receivable: row.accounts_receivable != null ? String(row.accounts_receivable) : "",
      work_in_progress: row.work_in_progress != null ? String(row.work_in_progress) : "",
      cash_balance: row.cash_balance != null ? String(row.cash_balance) : "",
    });
    setEditingMonth(row.month);
    // Scroll the entry form into view + focus the first field.
    setTimeout(() => {
      entryFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const cancelEdit = () => {
    setEditingMonth(null);
    setNewEntry({
      year: selectedYear,
      month: new Date().getMonth() + 1,
      accounts_receivable: "",
      work_in_progress: "",
      cash_balance: "",
    });
  };

  // Row currently pending deletion (holds {year, month}); null = dialog closed.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/financials/${pendingDelete.year}/${pendingDelete.month}`,
        { headers: getAuthHeader() }
      );
      toast.success(`${MONTHS[pendingDelete.month - 1]} ${pendingDelete.year} deleted`);
      // If the row being edited is the one just deleted, clear the form too.
      if (editingMonth === pendingDelete.month) cancelEdit();
      setPendingDelete(null);
      loadFinancials();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const getChangeIndicator = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    if (change > 0) {
      return <span className="text-red-500 text-xs flex items-center"><TrendingUp className="h-3 w-3 mr-1" />+{change.toFixed(1)}%</span>;
    } else if (change < 0) {
      return <span className="text-green-500 text-xs flex items-center"><TrendingDown className="h-3 w-3 mr-1" />{change.toFixed(1)}%</span>;
    }
    return <span className="text-muted-foreground text-xs">0%</span>;
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  // Calculate totals for current year
  const totals = comparison?.data?.reduce((acc, d) => ({
    ar: acc.ar + (d.accounts_receivable || 0),
    wip: acc.wip + (d.work_in_progress || 0),
    cash: acc.cash + (d.cash_balance || 0),
    prevAr: acc.prevAr + (d.prev_accounts_receivable || 0),
    prevWip: acc.prevWip + (d.prev_work_in_progress || 0),
    prevCash: acc.prevCash + (d.prev_cash_balance || 0),
  }), { ar: 0, wip: 0, cash: 0, prevAr: 0, prevWip: 0, prevCash: 0 }) || {};

  // Get latest values (most recent month)
  const latestData = financials[0] || {};

  return (
    <div className="space-y-6" data-testid="financials-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">A/R, WIP & Cash</h1>
          <p className="text-muted-foreground">Track monthly firm financials</p>
        </div>
        <Select value={selectedYear.toString()} onValueChange={v => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Accounts Receivable</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(latestData.accounts_receivable)}</div>
            <p className="text-xs text-muted-foreground mt-1">Latest month-end balance</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Work in Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(latestData.work_in_progress)}</div>
            <p className="text-xs text-muted-foreground mt-1">Latest month-end balance</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Cash Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{formatCurrency(latestData.cash_balance)}</div>
            <p className="text-xs text-muted-foreground mt-1">Latest month-end balance</p>
          </CardContent>
        </Card>
      </div>

      {/* Entry Form - Admin Only */}
      {isAdmin && (
        <Card ref={entryFormRef} data-testid="financials-entry-form">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {editingMonth ? (
                <>
                  <Pencil className="h-5 w-5" />
                  Editing {MONTHS[editingMonth - 1]} {newEntry.year}
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" /> Add/Update Monthly Data
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={newEntry.year.toString()} onValueChange={v => setNewEntry({...newEntry, year: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Month</Label>
                <Select value={newEntry.month.toString()} onValueChange={v => setNewEntry({...newEntry, month: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={(i+1).toString()}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>A/R Balance</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    type="number" 
                    value={newEntry.accounts_receivable} 
                    onChange={e => setNewEntry({...newEntry, accounts_receivable: e.target.value})}
                    placeholder="0"
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>WIP Balance</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    type="number" 
                    value={newEntry.work_in_progress} 
                    onChange={e => setNewEntry({...newEntry, work_in_progress: e.target.value})}
                    placeholder="0"
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cash Balance</Label>
                <div className="relative">
                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input 
                    type="number" 
                    value={newEntry.cash_balance} 
                    onChange={e => setNewEntry({...newEntry, cash_balance: e.target.value})}
                    placeholder="0"
                    className="pl-8"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  data-testid="financials-save-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingMonth ? "Save Changes" : "Save")}
                </Button>
                {editingMonth && (
                  <Button
                    onClick={cancelEdit}
                    variant="outline"
                    disabled={saving}
                    data-testid="financials-cancel-edit-btn"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Comparison Table */}
      <Card>
        <CardHeader>
          <CardTitle>Year-over-Year Comparison ({selectedYear} vs {selectedYear - 1})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">A/R</TableHead>
                  <TableHead className="text-right">Prior A/R</TableHead>
                  <TableHead className="text-right">WIP</TableHead>
                  <TableHead className="text-right">Prior WIP</TableHead>
                  <TableHead className="text-right">Cash</TableHead>
                  <TableHead className="text-right">Prior Cash</TableHead>
                  {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparison?.data?.map((row, i) => (
                  <TableRow
                    key={i}
                    data-testid={`financials-row-${row.month}`}
                    className={editingMonth === row.month ? "bg-blue-500/10" : ""}
                  >
                    <TableCell className="font-medium">{MONTHS[row.month - 1]}</TableCell>
                    <TableCell className="text-right">
                      <div>{formatCurrency(row.accounts_receivable)}</div>
                      {getChangeIndicator(row.accounts_receivable, row.prev_accounts_receivable)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatCurrency(row.prev_accounts_receivable)}</TableCell>
                    <TableCell className="text-right">
                      <div>{formatCurrency(row.work_in_progress)}</div>
                      {getChangeIndicator(row.work_in_progress, row.prev_work_in_progress)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatCurrency(row.prev_work_in_progress)}</TableCell>
                    <TableCell className="text-right">
                      <div>{formatCurrency(row.cash_balance)}</div>
                      {getChangeIndicator(row.cash_balance, row.prev_cash_balance)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatCurrency(row.prev_cash_balance)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => startEditRow({ ...row, year: selectedYear })}
                            data-testid={`financials-edit-btn-${row.month}`}
                            title="Edit this month"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-500/10"
                            onClick={() => setPendingDelete({ year: selectedYear, month: row.month })}
                            data-testid={`financials-delete-btn-${row.month}`}
                            title="Delete this month"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {(!comparison?.data || comparison.data.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-muted-foreground">
                      No data for {selectedYear}. {isAdmin && "Add entries using the form above."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && !deleting && setPendingDelete(null)}>
        <AlertDialogContent data-testid="financials-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this month&apos;s entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  This will permanently remove the A/R, WIP, and Cash figures
                  for <strong>{MONTHS[pendingDelete.month - 1]} {pendingDelete.year}</strong>.
                  Prior-year comparison rows for the same month are not affected.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="financials-delete-confirm-btn"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
